/**
 * A5 · Guided coach onboarding: invite students, pick (or build) a starter
 * template, assign it — all three steps on one page with live checkmarks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError } from '../../lib/api.ts';
import { Button, Card, ErrorText, Input, Spinner, Tag, cls } from '../../lib/ui.tsx';
import { exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';

interface OnboardingState {
  done: boolean;
  invitesSent: number;
  studentsJoined: number;
  plansAssigned: number;
  activated: boolean;
}

interface Invite {
  id: string;
  email: string | null;
  status: string;
  link: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  items: { id: string; exerciseId: string }[];
}

interface CatalogExercise {
  id: string;
  name: string;
  description: string;
  level: string;
  estDurationSec: number;
}

interface StudentRow {
  userId: string;
  status: string;
  user: { id: string; name: string; email: string };
}

function StepHeader({ n, done, title }: { n: number; done: boolean; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cls(
          'flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold',
          done ? 'bg-emerald-100 text-emerald-700' : 'bg-brand-100 text-brand-700',
        )}
      >
        {done ? '✓' : n}
      </span>
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
    </div>
  );
}

function StepCard({ n, done, title, children }: { n: number; done: boolean; title: string; children: ReactNode }) {
  return (
    <Card className={done ? 'border-emerald-200' : ''}>
      <StepHeader n={n} done={done} title={title} />
      <div className="mt-3 pl-10">{children}</div>
    </Card>
  );
}

export default function OnboardingPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const state = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => api<OnboardingState>('/api/onboarding'),
  });
  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<Template[]>('/api/templates'),
  });
  const catalog = useQuery({
    queryKey: ['exercises', { tag: 'warm-up' }],
    queryFn: () => api<CatalogExercise[]>('/api/exercises?tag=warm-up'),
  });
  const students = useQuery({
    queryKey: ['students'],
    queryFn: () => api<StudentRow[]>('/api/students'),
  });

  // Step 1 · invites
  const [email, setEmail] = useState('');
  const [lastInvite, setLastInvite] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);
  const createInvite = useMutation({
    mutationFn: (inviteEmail?: string) =>
      api<Invite>('/api/auth/invites', { body: inviteEmail ? { email: inviteEmail } : {} }),
    onSuccess: (invite) => {
      setLastInvite(invite);
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
      void qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });

  // Step 2 · starter template
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [checkedExercises, setCheckedExercises] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!selectedTemplateId && templates.data && templates.data.length > 0) {
      setSelectedTemplateId(templates.data[0]?.id ?? null);
    }
  }, [templates.data, selectedTemplateId]);

  const createTemplate = useMutation({
    mutationFn: () =>
      api<Template>('/api/templates', {
        body: {
          name: 'My first week',
          items: [...checkedExercises].map((exerciseId) => ({ exerciseId, frequencyPerWeek: 3 })),
        },
      }),
    onSuccess: (tpl) => {
      setSelectedTemplateId(tpl.id);
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  // Step 3 · assign
  const [checkedStudents, setCheckedStudents] = useState<Set<string>>(new Set());
  const assign = useMutation({
    mutationFn: () =>
      api('/api/plans/from-template', {
        body: { templateId: selectedTemplateId, studentIds: [...checkedStudents] },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['onboarding'] }),
  });

  const complete = useMutation({
    mutationFn: () => api('/api/onboarding/complete', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
      navigate('/coach');
    },
  });

  if (state.isLoading) return <Spinner />;
  if (state.error) return <ErrorText error={state.error} />;
  if (!state.data) return null;

  const s = state.data;
  const step1Done = s.invitesSent >= 3;
  const step2Done = (templates.data?.length ?? 0) > 0;
  const step3Done = s.plansAssigned >= 1;

  function toggle(set: Set<string>, id: string, apply: (next: Set<string>) => void) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="text-lg font-bold">Welcome to Solfo 👋</h1>
        <p className="text-sm text-slate-500">Three small steps and your studio is up and running.</p>
      </div>

      {s.activated && (
        <Card className="border-emerald-200 bg-emerald-50 text-center">
          <p className="text-base font-semibold text-emerald-900">🎉 You're all set!</p>
          <p className="mt-1 text-sm text-emerald-800">
            Invites are out and your first plan is assigned. Your dashboard will fill up as students practice.
          </p>
          <Button className="mt-3" onClick={() => complete.mutate()} disabled={complete.isPending}>
            Go to my dashboard →
          </Button>
          <ErrorText error={complete.error} />
        </Card>
      )}

      <StepCard n={1} done={step1Done} title="Invite 2–3 students">
        <p className="text-xs text-slate-500">
          {s.invitesSent} invite{s.invitesSent === 1 ? '' : 's'} sent · {s.studentsJoined} joined
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createInvite.mutate(email.trim() || undefined);
          }}
          className="mt-2 flex gap-2"
        >
          <Input
            type="email"
            placeholder="student@email.com (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" disabled={createInvite.isPending} className="shrink-0">
            Invite
          </Button>
        </form>
        <ErrorText error={createInvite.error} />
        {lastInvite && (
          <div className="mt-2 flex items-center gap-2 rounded-lg bg-slate-50 p-2">
            <code className="min-w-0 flex-1 truncate text-xs text-slate-700">{lastInvite.link}</code>
            <Button
              variant="secondary"
              className="shrink-0"
              onClick={() => {
                void navigator.clipboard.writeText(lastInvite.link);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? 'Copied ✓' : 'Copy link'}
            </Button>
          </div>
        )}
      </StepCard>

      <StepCard n={2} done={step2Done} title="Pick a starter template">
        {templates.isLoading || catalog.isLoading ? (
          <Spinner />
        ) : step2Done ? (
          <div className="space-y-1.5">
            {(templates.data ?? []).map((tpl) => (
              <label key={tpl.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                <input
                  type="radio"
                  name="starter-template"
                  checked={selectedTemplateId === tpl.id}
                  onChange={() => setSelectedTemplateId(tpl.id)}
                />
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{tpl.name}</span>
                <Tag>{tpl.items.length} exercises</Tag>
              </label>
            ))}
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              No templates yet — pick a few warm-ups from the catalog and we'll bundle them into “My first week”
              (3× per week each).
            </p>
            <div className="mt-2 max-h-56 space-y-1.5 overflow-y-auto">
              {(catalog.data ?? []).map((ex) => (
                <label key={ex.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={checkedExercises.has(ex.id)}
                    onChange={() => toggle(checkedExercises, ex.id, setCheckedExercises)}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{exLabel(ex)}</span>
                  <span className="shrink-0 text-xs text-slate-400">{Math.round(ex.estDurationSec / 60)} min</span>
                </label>
              ))}
            </div>
            <Button
              className="mt-3"
              disabled={checkedExercises.size === 0 || createTemplate.isPending}
              onClick={() => createTemplate.mutate()}
            >
              Create “My first week” ({checkedExercises.size})
            </Button>
            <ErrorText error={createTemplate.error} />
          </>
        )}
      </StepCard>

      <StepCard n={3} done={step3Done} title="Assign it to your students">
        {students.isLoading ? (
          <Spinner />
        ) : (students.data ?? []).filter((st) => st.status === 'active').length === 0 ? (
          <p className="text-xs text-slate-500">
            No students have joined yet — as soon as someone accepts an invite, you can assign the plan here.
          </p>
        ) : (
          <>
            <div className="space-y-1.5">
              {(students.data ?? [])
                .filter((st) => st.status === 'active')
                .map((st) => (
                  <label key={st.userId} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={checkedStudents.has(st.userId)}
                      onChange={() => toggle(checkedStudents, st.userId, setCheckedStudents)}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{st.user.name}</span>
                    <span className="shrink-0 text-xs text-slate-400">{st.user.email}</span>
                  </label>
                ))}
            </div>
            <Button
              className="mt-3"
              disabled={!selectedTemplateId || checkedStudents.size === 0 || assign.isPending}
              onClick={() => assign.mutate()}
            >
              Assign plan ({checkedStudents.size})
            </Button>
            {assign.isSuccess && (
              <p className="mt-2 text-sm text-emerald-700">Plan assigned — transposed to each student's range. ✓</p>
            )}
            <ErrorText error={assign.error} />
          </>
        )}
      </StepCard>
    </div>
  );
}
